import React, { useEffect, useState } from 'react';
import { ArrowUp, Mic, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { TemporaryChatButton } from '../../features/chat/TemporaryChatButton';
import { RismanServicesSection } from '../../features/home/RismanServicesSection';
import { ModelQuickDropdown } from '../../features/models/ModelQuickDropdown';
import { useI18n } from '../../hooks/useI18n';
import { loadStoredModel } from '../../constants/chatImageModels';
import { useLlmCatalog } from '../../hooks/useLlmCatalog';

export const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { models } = useLlmCatalog();
  const [chatPrompt, setChatPrompt] = useState('');
  const [model, setModel] = useState(() => loadStoredModel('chat'));
  const [isTemporary, setIsTemporary] = useState(false);

  useEffect(() => {
    setModel(loadStoredModel('chat', models));
  }, [models]);

  const startChat = (event) => {
    event?.preventDefault?.();
    const text = chatPrompt.trim();
    const path = isTemporary ? '/chat?temporary=1' : '/chat';
    if (!text) {
      navigate(path);
      return;
    }
    navigate(path, { state: { initialPrompt: text } });
  };

  return (
    <DashboardLayout
      contentClassName="flex min-h-[calc(100dvh-5.5rem)] flex-col py-6 sm:py-8"
      startActions={
        <ModelQuickDropdown kind="chat" modelKey={model} returnTo="/home" onChange={setModel} />
      }
      actions={
        <TemporaryChatButton active={isTemporary} onClick={() => setIsTemporary((value) => !value)} />
      }
    >
      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col">
        <section className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center">
          {isTemporary ? (
            <div className="mb-8 max-w-xl text-center mx-auto">
              <h2 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">
                {t('chatTemporary')}
              </h2>
              <p className="mt-3 text-sm leading-7 text-muted">
                {t('chatTemporaryDescription')}
              </p>
            </div>
          ) : (
            <h2 className="mb-8 whitespace-nowrap text-center text-[clamp(1.25rem,4.8vw,2.25rem)] font-bold tracking-tight text-text">
              {t('chatGreeting')}
            </h2>
          )}
          <form onSubmit={startChat} className="w-full">
            <div className="overflow-hidden rounded-[28px] border border-transparent bg-accent-soft dark:border-border dark:bg-panel-strong">
              <div className="flex items-end gap-1 px-2 py-2">
                <button
                  type="button"
                  onClick={() => navigate(isTemporary ? '/chat?temporary=1' : '/chat')}
                  className="mb-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted transition hover:bg-accent-soft hover:text-text dark:hover:opacity-95"
                  aria-label={t('chatToolAttach')}
                >
                  <Plus size={18} />
                </button>
                <textarea
                  value={chatPrompt}
                  onChange={(event) => setChatPrompt(event.target.value)}
                  rows={1}
                  placeholder={t('chatAskPlaceholder')}
                  className="max-h-32 min-h-[40px] flex-1 resize-none bg-transparent py-2.5 text-base outline-none placeholder:text-muted md:text-sm"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      startChat(event);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => navigate(isTemporary ? '/chat?temporary=1' : '/chat')}
                  className="mb-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted transition hover:bg-accent-soft hover:text-text dark:hover:opacity-95"
                  title={t('chatVoiceInput')}
                >
                  <Mic size={16} />
                </button>
                {chatPrompt.trim() ? (
                  <button
                    type="submit"
                    className="mb-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-white transition hover:opacity-95 dark:text-slate-950"
                    aria-label={t('send')}
                  >
                    <ArrowUp size={16} />
                  </button>
                ) : null}
              </div>
            </div>
          </form>
        </section>

        <RismanServicesSection className="mt-10 sm:mt-12" isTemporary={isTemporary} />
      </div>
    </DashboardLayout>
  );
};
